import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCustomer } from '../lib/api';
import { useApi } from '../hooks/useApi';
import CustomerActions from './CustomerActions';
import InteractionHistory from './InteractionHistory';
import type { Customer, Interaction } from '../types';

export default function CustomerDetails() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, execute } = useApi(getCustomer);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [interactions, setInteractions] = useState<Interaction[]>([]);

  useEffect(() => {
    if (id) {
      execute(id);
    }
  }, [id, execute]);

  useEffect(() => {
    if (data) {
      setCustomer(data.customer);
      setInteractions(data.interactions);
    }
  }, [data]);

  const refreshInteractions = async () => {
    if (id) {
      const result = await execute(id);
      if (result) {
        setInteractions(result.interactions);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-sm text-red-700">{error}</p>
        <Link to="/search" className="mt-2 inline-block text-sm text-primary-600 hover:underline">
          Back to Search
        </Link>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Customer not found.</p>
        <Link to="/search" className="mt-2 inline-block text-sm text-primary-600 hover:underline">
          Back to Search
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/search" className="text-sm text-primary-600 hover:underline mb-2 inline-block">
            &larr; Back to Search
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {customer.first_name} {customer.last_name}
          </h1>
          <p className="text-sm text-gray-500">{customer.email}</p>
        </div>
        <div className="flex items-center space-x-2">
          {customer.is_closed ? (
            <span className="badge-danger">Account Closed</span>
          ) : customer.is_dge_excluded ? (
            <span className="badge-warning">DGE Excluded</span>
          ) : (
            <span className="badge-success">Active</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h2>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">External ID</dt>
                <dd className="text-sm text-gray-900 font-mono">{customer.external_id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Internal ID</dt>
                <dd className="text-sm text-gray-900 font-mono">{customer.id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Date of Birth</dt>
                <dd className="text-sm text-gray-900">{customer.dob || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">SSN (Last 4)</dt>
                <dd className="text-sm text-gray-900">
                  {customer.ssn_last4 ? `***-**-${customer.ssn_last4}` : 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Phone</dt>
                <dd className="text-sm text-gray-900">{customer.phone || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Created At</dt>
                <dd className="text-sm text-gray-900">
                  {new Date(customer.created_at).toLocaleDateString()}
                </dd>
              </div>
            </dl>

            {(customer.address_line1 || customer.city) && (
              <>
                <hr className="my-4" />
                <h3 className="text-sm font-medium text-gray-900 mb-2">Address</h3>
                <address className="text-sm text-gray-600 not-italic">
                  {customer.address_line1 && <div>{customer.address_line1}</div>}
                  {customer.address_line2 && <div>{customer.address_line2}</div>}
                  {(customer.city || customer.state || customer.zip) && (
                    <div>
                      {customer.city}
                      {customer.city && customer.state && ', '}
                      {customer.state} {customer.zip}
                    </div>
                  )}
                </address>
              </>
            )}
          </div>

          <InteractionHistory
            interactions={interactions}
            customerId={customer.id}
            customerEmail={customer.email}
            onInteractionCreated={refreshInteractions}
          />
        </div>

        {/* Actions Sidebar */}
        <div>
          <CustomerActions customer={customer} onActionComplete={refreshInteractions} />
        </div>
      </div>
    </div>
  );
}
